import React, { useRef, useState } from 'react';
import { ImageIcon, Images, Loader2, Monitor, PanelBottom, PanelTop, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

import { mediaAPI } from '../lib/api';
import CloudinaryMediaLibraryDialog from './CloudinaryMediaLibraryDialog';
import { Button } from './ui/button';


const BRANDING_SLOTS = [
  {
    key: 'site_logo',
    label: 'Storefront Site Logo',
    description: 'Primary website logo used as the fallback across the storefront.',
    recommendation: 'Required canvas: 1200 × 400 px (3:1) · transparent PNG/WebP',
    requiredWidth: 1200, requiredHeight: 400,
    icon: ImageIcon,
    square: false,
  },
  {
    key: 'header_logo',
    label: 'Storefront Header Logo',
    description: 'Displayed in the storefront navigation header.',
    recommendation: 'Required canvas: 1200 × 400 px (3:1) · transparent PNG/WebP',
    requiredWidth: 1200, requiredHeight: 400,
    icon: PanelTop,
    square: false,
  },
  {
    key: 'footer_logo',
    label: 'Storefront Footer Logo',
    description: 'Displayed in the storefront footer.',
    recommendation: 'Required canvas: 1200 × 400 px (3:1) · transparent PNG/WebP',
    requiredWidth: 1200, requiredHeight: 400,
    icon: PanelBottom,
    square: false,
  },
  {
    key: 'storefront_browser_logo',
    label: 'Storefront Browser Logo',
    description: 'Displayed in the customer-site browser tab.',
    recommendation: 'Required canvas: 512 × 512 px · square PNG/WebP',
    requiredWidth: 512, requiredHeight: 512,
    icon: Monitor,
    square: true,
  },
  {
    key: 'erp_browser_logo',
    label: 'ERP Browser Logo',
    description: 'Displayed in the ERP browser tab.',
    recommendation: 'Required canvas: 512 × 512 px · square PNG/WebP',
    requiredWidth: 512, requiredHeight: 512,
    icon: Monitor,
    square: true,
  },
];

const transformedPreview = (url, square) => {
  if (!url?.includes('/image/upload/')) return url;
  const transformation = square
    ? 'f_auto,q_auto:eco,c_fit,w_256,h_256,b_transparent'
    : 'f_auto,q_auto:eco,c_fit,w_640,h_240,b_transparent';
  return url.replace('/image/upload/', `/image/upload/${transformation}/`);
};

export default function StorefrontBrandingManager({
  branding = {},
  companyLogo = null,
  onChange,
  disabled = false,
  showHeader = true,
}) {
  const inputRef = useRef(null);
  const [activeSlot, setActiveSlot] = useState('');
  const [uploadingSlot, setUploadingSlot] = useState('');
  const [librarySlot, setLibrarySlot] = useState('');

  const chooseFile = (slot) => {
    setActiveSlot(slot);
    inputRef.current?.click();
  };

  const applyAsset = (slotKey, uploaded, message = 'selected') => {
    const slot = BRANDING_SLOTS.find((item) => item.key === slotKey);
    if (slot && uploaded?.width && uploaded?.height) {
      const actualRatio = uploaded.width / uploaded.height;
      const requiredRatio = slot.requiredWidth / slot.requiredHeight;
      const ratioDifference = Math.abs(actualRatio - requiredRatio) / requiredRatio;
      if (ratioDifference > 0.06 || uploaded.width < slot.requiredWidth || uploaded.height < slot.requiredHeight) {
        toast.warning(`${slot.label}: use ${slot.requiredWidth} × ${slot.requiredHeight} px for the best result. Selected: ${uploaded.width} × ${uploaded.height} px.`);
      }
    }
    onChange({
      ...branding,
      [slotKey]: {
        ...uploaded,
        alt: uploaded?.alt || slot?.label || 'URBNSW brand asset',
      },
    });
    toast.success(`${slot?.label || 'Brand asset'} ${message}. Save the storefront settings to publish it.`);
  };

  const uploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !activeSlot) return;

    const slot = BRANDING_SLOTS.find((item) => item.key === activeSlot);
    setUploadingSlot(activeSlot);
    try {
      const uploaded = await mediaAPI.uploadFile(file);
      applyAsset(activeSlot, uploaded, 'uploaded');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.response?.data?.detail || error.message || 'Image upload failed');
    } finally {
      setUploadingSlot('');
      setActiveSlot('');
    }
  };

  const remove = (slot) => onChange({ ...branding, [slot]: null });

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        onChange={uploadFile}
        className="hidden"
      />

      {showHeader && (
        <div>
          <h3 className="font-semibold text-slate-900">Storefront & ERP Brand Assets</h3>
          <p className="mt-1 text-sm text-slate-500">
            These images are independent from the Company Logo. If a dedicated asset is missing, the Company Logo is used as a fallback.
          </p>
        </div>
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-5 text-blue-900">
        <strong>Required image sizes:</strong> header/footer logos 1200 × 400 px; browser icons 512 × 512 px. Use PNG or WebP, transparent background, and keep the logo centred inside the canvas.
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {BRANDING_SLOTS.map((slot) => {
          const asset = branding?.[slot.key] || null;
          const previewAsset = asset || companyLogo;
          const uploading = uploadingSlot === slot.key;
          const SlotIcon = slot.icon;

          return (
            <article key={slot.key} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-slate-900 p-2 text-lime-300">
                  <SlotIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-slate-900">{slot.label}</h4>
                  <p className="mt-1 text-xs leading-5 text-slate-500">{slot.description}</p>
                </div>
              </div>

              <div className={`mt-4 flex items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white p-4 ${slot.square ? 'mx-auto aspect-square max-w-40' : 'aspect-[3/1] w-full'}`}>
                {previewAsset?.url ? (
                  <img
                    src={transformedPreview(previewAsset.url, slot.square)}
                    alt={previewAsset.alt || slot.label}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center text-slate-300">
                    <ImageIcon className="h-8 w-8" />
                    <span className="mt-2 text-xs">No image</span>
                  </div>
                )}
              </div>

              <div className="mt-3 flex min-h-5 items-center justify-between gap-2 text-[11px]">
                <span className="text-slate-500">{slot.recommendation}</span>
                {!asset && companyLogo?.url ? (
                  <span className="shrink-0 rounded-full bg-amber-100 px-2 py-1 font-medium text-amber-700">Company fallback</span>
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setLibrarySlot(slot.key)}
                  disabled={disabled || uploading}
                  className="flex-1"
                >
                  <Images /> Choose existing
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => chooseFile(slot.key)}
                  disabled={disabled || uploading}
                  className="flex-1"
                >
                  {uploading ? <Loader2 className="animate-spin" /> : <UploadCloud />}
                  {asset ? 'Upload replacement' : 'Upload new'}
                </Button>
                {asset ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => remove(slot.key)}
                    disabled={disabled || uploading}
                    className="text-red-600 hover:text-red-700"
                    aria-label={`Remove ${slot.label}`}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>

      <CloudinaryMediaLibraryDialog
        open={Boolean(librarySlot)}
        onOpenChange={(open) => { if (!open) setLibrarySlot(''); }}
        title="Choose brand image from Cloudinary"
        onSelect={(asset) => {
          applyAsset(librarySlot, asset, 'selected from Cloudinary');
          setLibrarySlot('');
        }}
      />
    </section>
  );
}
